/** Start of local calendar day for a date-like value (ms). */
export const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

const addDays = (dayStartMs, n) => {
  const x = new Date(dayStartMs);
  x.setDate(x.getDate() + n);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

const localYmd = (ms) => {
  const x = new Date(ms);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Sunday (start of day) of the calendar week containing `d`. */
export const sundayEndOfWeekContaining = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay();
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
  x.setDate(x.getDate() + daysUntilSunday);
  return x.getTime();
};

/**
 * Best single "expiry / next relevant day" for grouping (start of that day, ms).
 * Returns null for recurring / open-ended deals (no fixed calendar end).
 * For specific_dates with only past dates, returns the latest past day so callers
 * can treat exp < today as expired (omit from list).
 */
export const getDealExpiryDayStart = (deal, now = new Date()) => {
  const v = deal?.validity;
  if (!v?.type) return null;

  const todayStart = startOfDay(now);

  if (v.type === "range" && v.end) {
    return startOfDay(new Date(v.end));
  }

  if (v.type === "specific_dates" && Array.isArray(v.specific_dates)) {
    const parsed = v.specific_dates
      .map((s) => startOfDay(new Date(s)))
      .filter((ts) => !Number.isNaN(ts))
      .sort((a, b) => a - b);

    if (parsed.length === 0) return todayStart - 1;

    const upcoming = parsed.filter((ts) => ts >= todayStart);
    if (upcoming.length > 0) return upcoming[0];

    return parsed[parsed.length - 1];
  }

  if (
    v.type === "recurring_weekly" ||
    v.type === "recurring_weekdays" ||
    v.type === "recurring_weekends" ||
    v.type === "recurring_monthly" ||
    v.type === "not_applicable"
  ) {
    return null;
  }

  return null;
};

/**
 * @returns {"today"|"tomorrow"|"thisWeek"|"later"|null} null = omit (expired / no bucket)
 */
export const getDealExpiryBucket = (deal, now = new Date()) => {
  const exp = getDealExpiryDayStart(deal);
  const todayStart = startOfDay(now);
  const tomorrowStart = addDays(todayStart, 1);
  const weekEndSunday = sundayEndOfWeekContaining(now);

  if (exp === null) return "later";

  if (exp < todayStart) return null;

  const yExp = localYmd(exp);
  if (yExp === localYmd(todayStart)) return "today";
  if (yExp === localYmd(tomorrowStart)) return "tomorrow";

  if (exp > tomorrowStart && exp <= weekEndSunday) return "thisWeek";
  return "later";
};

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_ABBR = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

const DAY_UPPER = [
  "SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY",
];

/** e.g. "24 MAY 2026 (TODAY)" or "22 MAY 2026 (PAST FRIDAY)" */
export const formatAvailabilityDayLabel = (date, now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.round(
    (target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
  );
  const datePart = `${target.getDate()} ${MONTH_ABBR[target.getMonth()]} ${target.getFullYear()}`;
  const dayName = DAY_UPPER[target.getDay()];

  if (diffDays === 0) return `${datePart} (TODAY)`;
  if (diffDays === 1) return `${datePart} (TOMORROW)`;
  if (diffDays === -1) return `${datePart} (YESTERDAY)`;
  if (diffDays > 1) return `${datePart} (NEXT ${dayName})`;
  return `${datePart} (PAST ${dayName})`;
};

const jsDayToCustomDay = (jsDay) => (jsDay === 0 ? 7 : jsDay);

const recurringWeeklyMatchesDay = (validity, dayDate) => {
  const days = validity.daysOfWeek ?? validity.days_of_week;
  if (!Array.isArray(days) || days.length === 0) return true;
  if (typeof days[0] === "number") {
    return days.includes(jsDayToCustomDay(dayDate.getDay()));
  }
  const dayName = DAY_NAMES[dayDate.getDay()].toLowerCase();
  return days.some((d) => {
    const dl = String(d).toLowerCase();
    return dl === dayName || dl === dayName.slice(0, 3);
  });
};

/** Last calendar day (Sunday) of next week, start of day ms. */
export const getNextWeekSundayStart = (now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const dow = today.getDay();
  const daysUntilNextMonday = dow === 0 ? 1 : 8 - dow;
  const nextSunday = new Date(today);
  nextSunday.setDate(today.getDate() + daysUntilNextMonday + 6);
  nextSunday.setHours(0, 0, 0, 0);
  return nextSunday.getTime();
};

/** Pill label: "Today" or "Tue 3". */
export const formatAvailableDayChipLabel = (date, now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  if (localYmd(target.getTime()) === localYmd(today.getTime())) return "Today";
  return `${DAY_ABBR[target.getDay()]} ${target.getDate()}`;
};

/**
 * Day pills from today through end of next week (no past dates).
 * @returns {{ dateKey: string, label: string, date: Date, isToday: boolean }[]}
 */
export const getAvailableDayChips = (now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayKey = localYmd(today.getTime());
  const endMs = getNextWeekSundayStart(now);

  const chips = [];
  const cursor = new Date(today);
  while (cursor.getTime() <= endMs) {
    const dateKey = localYmd(cursor.getTime());
    chips.push({
      dateKey,
      label: formatAvailableDayChipLabel(cursor, now),
      date: new Date(cursor),
      isToday: dateKey === todayKey,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return chips;
};

/**
 * Returns day chips for all 7 days of the current week (Mon–Sun),
 * regardless of what day today is. Today's dateKey is used as the default selection.
 * @returns {{ dateKey: string, label: string, date: Date, isToday: boolean }[]}
 */
export const getThisWeekDayChips = (now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayKey = localYmd(today.getTime());

  // Find the Monday of the current week
  const dow = today.getDay(); // 0=Sun,1=Mon,...,6=Sat
  const daysToMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + daysToMonday);

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    const dateKey = localYmd(date.getTime());
    return {
      dateKey,
      label: DAY_ABBR[date.getDay()],
      date,
      isToday: dateKey === todayKey,
    };
  });
};

/**
 * Returns day chips for Mon–Sun of next calendar week.
 * @returns {{ dateKey: string, label: string, date: Date }[]}
 */
export const getNextWeekDayChips = (now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const dow = today.getDay();
  const daysUntilNextMonday = dow === 0 ? 1 : 8 - dow;
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() + daysUntilNextMonday + i);
    return {
      dateKey: localYmd(date.getTime()),
      label: DAY_ABBR[date.getDay()],
      date,
    };
  });
};

/**
 * Returns true if the deal is available on the given day.
 * Handles all validity types: recurring_weekly, recurring_weekdays,
 * recurring_weekends, specific_dates, range, not_applicable.
 */
export const isDealAvailableOnDay = (deal, dayDate) => {
  const v = deal?.validity;
  if (!v?.type || v.type === "not_applicable") return true;

  const dow = dayDate.getDay();

  if (v.type === "recurring_weekly") {
    return recurringWeeklyMatchesDay(v, dayDate);
  }
  if (v.type === "recurring_weekdays") return dow >= 1 && dow <= 5;
  if (v.type === "recurring_weekends") return dow === 0 || dow === 6;
  if (v.type === "specific_dates") {
    const ymd = localYmd(dayDate.getTime());
    return (
      Array.isArray(v.specific_dates) &&
      v.specific_dates.some(
        (d) => typeof d === "string" && d.slice(0, 10) === ymd,
      )
    );
  }
  if (v.type === "range") {
    const dayMs = startOfDay(dayDate);
    const startMs = v.start ? startOfDay(new Date(v.start)) : 0;
    const endMs = v.end ? startOfDay(new Date(v.end)) : Infinity;
    return dayMs >= startMs && dayMs <= endMs;
  }
  return true;
};

/**
 * Groups deals into per-day availability buckets for tomorrow through
 * end of the current week (Sunday). Today is excluded — callers render
 * today separately. Returns only buckets that have at least one deal.
 *
 * @returns {{ dateKey: string, label: string, deals: object[] }[]}
 */
export const getWeekDayBuckets = (deals, now = new Date()) => {
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const dow = todayStart.getDay();
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;

  const buckets = [];
  for (let i = 1; i <= daysUntilSunday; i++) {
    const date = new Date(todayStart);
    date.setDate(date.getDate() + i);

    const label =
      i === 1
        ? "AVAILABLE TOMORROW"
        : `AVAILABLE ${DAY_NAMES[date.getDay()].toUpperCase()}`;

    const dateKey = localYmd(date.getTime());
    const dayDeals = deals.filter((deal) => isDealAvailableOnDay(deal, date));
    buckets.push({ dateKey, label, deals: dayDeals });
  }

  return buckets.filter((b) => b.deals.length > 0);
};

/** True when a deal is inactive or its fixed validity window has ended. */
export const isDealExpired = (deal, now = new Date()) => {
  if (!deal) return false;
  if (deal._expired === true || deal.isActive === false) return true;

  const exp = getDealExpiryDayStart(deal, now);
  if (exp === null) return false;

  return exp < startOfDay(now);
};

const ALL_DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 7];

/** Sort tier: lower = higher in the list. */
export const DEAL_VALIDITY_SORT_RANK = {
  EXPIRING: 0,
  SPECIFIC_DAYS: 1,
  EVERYDAY: 2,
  NO_DATES: 3,
};

export const isEverydayRecurringWeekly = (validity) => {
  if (!validity || validity.type !== "recurring_weekly") return false;
  const days = validity.daysOfWeek ?? validity.days_of_week;
  if (!Array.isArray(days) || days.length === 0) return true;

  if (typeof days[0] === "number") {
    const set = new Set(days.filter((d) => d >= 1 && d <= 7));
    return ALL_DAYS_OF_WEEK.every((d) => set.has(d));
  }

  const names = new Set(days.map((d) => String(d).toLowerCase()));
  return (
    DAY_NAMES.every((n) => names.has(n.toLowerCase())) ||
    DAY_ABBR.every((n) => names.has(n.toLowerCase()))
  );
};

/**
 * Default list order: expiring/specific dates → specific weekdays → everyday → no dates.
 */
/**
 * How many distinct days per week (or month) a deal runs on.
 * Lower = more specific (e.g. Thursday=1 above Mon–Fri=5).
 */
export const getDealSpecificDaysCount = (validity) => {
  if (!validity?.type) return Number.MAX_SAFE_INTEGER;

  if (validity.type === "recurring_weekdays") return 5;
  if (validity.type === "recurring_weekends") return 2;

  if (validity.type === "recurring_monthly") {
    const dom = validity.daysOfMonth ?? validity.days_of_month;
    return Array.isArray(dom) && dom.length > 0 ? dom.length : 31;
  }

  if (validity.type === "recurring_weekly") {
    const days = validity.daysOfWeek ?? validity.days_of_week;
    if (!Array.isArray(days) || days.length === 0) return 7;

    if (typeof days[0] === "number") {
      return new Set(days.filter((d) => d >= 1 && d <= 7)).size;
    }

    return new Set(days.map((d) => String(d).toLowerCase())).size;
  }

  return Number.MAX_SAFE_INTEGER;
};

export const getDealValiditySortRank = (deal) => {
  const v = deal?.validity;
  if (!v?.type || v.type === "not_applicable") {
    return DEAL_VALIDITY_SORT_RANK.NO_DATES;
  }

  switch (v.type) {
    case "specific_dates":
    case "range":
      return DEAL_VALIDITY_SORT_RANK.EXPIRING;
    case "recurring_weekdays":
    case "recurring_weekends":
    case "recurring_monthly":
      return DEAL_VALIDITY_SORT_RANK.SPECIFIC_DAYS;
    case "recurring_weekly":
      return isEverydayRecurringWeekly(v)
        ? DEAL_VALIDITY_SORT_RANK.EVERYDAY
        : DEAL_VALIDITY_SORT_RANK.SPECIFIC_DAYS;
    default:
      return DEAL_VALIDITY_SORT_RANK.NO_DATES;
  }
};

export const compareDealsByValidityPriority = (a, b, now = new Date()) => {
  const ra = getDealValiditySortRank(a);
  const rb = getDealValiditySortRank(b);
  if (ra !== rb) return ra - rb;

  if (ra === DEAL_VALIDITY_SORT_RANK.EXPIRING) {
    const ea = getDealExpiryDayStart(a, now);
    const eb = getDealExpiryDayStart(b, now);
    if (ea != null && eb != null && ea !== eb) return ea - eb;
    if (ea != null && eb == null) return -1;
    if (ea == null && eb != null) return 1;
  }

  if (ra === DEAL_VALIDITY_SORT_RANK.SPECIFIC_DAYS) {
    const ca = getDealSpecificDaysCount(a?.validity);
    const cb = getDealSpecificDaysCount(b?.validity);
    if (ca !== cb) return ca - cb;
  }

  return 0;
};

export const sortDealsByValidityPriority = (deals, now = new Date()) =>
  [...deals].sort((a, b) => compareDealsByValidityPriority(a, b, now));

export const groupDealsByExpiryBucket = (deals, now = new Date()) => {
  const buckets = {
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
  };

  for (const deal of deals) {
    const b = getDealExpiryBucket(deal, now);
    if (!b) continue;
    buckets[b].push(deal);
  }

  for (const key of Object.keys(buckets)) {
    buckets[key] = sortDealsByValidityPriority(buckets[key], now);
  }

  return buckets;
};
